/**
 * Shared dialog for suggesting a photo change on a product. Used from both
 * /products (admin list) and /marketing (the "Con fotos" tab) so the
 * reviewer flow stays consistent across the app.
 *
 * Props:
 *  - product: { id, name, brand, photos? } | null  (null = closed)
 *  - onClose: () => void
 *  - onSubmitted?: (suggestion) => void  fired after a successful POST
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChatCircleText, ArrowSquareOut, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SuggestPhotoDialog({ product, onClose, onSubmitted }) {
  const [message, setMessage] = useState("");
  const [referenceUrl, setReferenceUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [existing, setExisting] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!product) return;
    setMessage(""); setReferenceUrl("");
    setLoading(true);
    api.get(`/products/${product.id}/photo-suggestions`)
      .then((r) => setExisting(Array.isArray(r.data) ? r.data : []))
      .catch(() => setExisting([]))
      .finally(() => setLoading(false));
  }, [product]);

  const submit = async () => {
    if (message.trim().length < 3) {
      toast.error("Describe el cambio que quieres sugerir (mín. 3 caracteres).");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/products/${product.id}/photo-suggestions`, {
        message: message.trim(),
        reference_url: referenceUrl.trim() || "",
      });
      toast.success("Sugerencia enviada a Marketing");
      setExisting([data, ...existing]);
      setMessage(""); setReferenceUrl("");
      onSubmitted?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar la sugerencia");
    } finally { setSubmitting(false); }
  };

  const cancel = async (sid) => {
    try {
      await api.delete(`/photo-suggestions/${sid}`);
      setExisting(existing.filter((s) => s.id !== sid));
      toast.success("Sugerencia eliminada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-2xl">
            <ChatCircleText size={22} weight="duotone" className="text-amber-600" />
            Sugerir cambio de fotos
          </DialogTitle>
        </DialogHeader>
        {product && (
          <div className="space-y-4">
            <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3 flex items-center gap-3">
              {(product.photos || [])[0] && <img src={product.photos[0]} alt="" className="w-12 h-12 object-cover rounded border border-zinc-200" />}
              <div>
                <div className="font-semibold text-sm">{product.name}</div>
                {product.brand && <div className="text-xs text-zinc-500">{product.brand}</div>}
              </div>
            </div>

            {existing.length > 0 && (
              <div className="space-y-2 max-h-44 overflow-y-auto" data-testid="existing-suggestions">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Sugerencias previas para este producto</p>
                {existing.map((s) => (
                  <div key={s.id} className={`p-2.5 rounded border text-xs ${s.status === "pending" ? "bg-amber-50 border-amber-200" : "bg-zinc-50 border-zinc-200"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${s.status === "pending" ? "bg-amber-200 text-amber-900" : s.status === "resolved" ? "bg-emerald-200 text-emerald-900" : "bg-zinc-200 text-zinc-700"}`}>
                          {s.status === "pending" ? "Pendiente" : s.status === "resolved" ? "Resuelta" : "Descartada"}
                        </span>
                        <span className="text-[10px] text-zinc-500 ml-2">{new Date(s.created_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}</span>
                      </div>
                      {s.status === "pending" && (
                        <button onClick={() => cancel(s.id)} className="text-red-600 hover:bg-red-100 p-1 rounded" title="Eliminar sugerencia" data-testid={`del-suggestion-${s.id}`}>
                          <Trash size={11} />
                        </button>
                      )}
                    </div>
                    <p className="mt-1 text-zinc-800">{s.message}</p>
                    {s.reference_url && (
                      <a href={s.reference_url} target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline inline-flex items-center gap-1 mt-1">
                        <ArrowSquareOut size={10} /> Referencia
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div>
              <Label className="text-xs uppercase tracking-wider">¿Qué quieres mejorar? *</Label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Ej: la foto sale borrosa, falta fondo blanco, no se ve el logo, etc."
                rows={4}
                className="mt-1 w-full border border-zinc-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                data-testid="suggest-message"
                maxLength={1000}
              />
              <p className="text-[10px] text-zinc-400 mt-1">{message.length}/1000</p>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider">Link de referencia (opcional)</Label>
              <Input
                type="url"
                value={referenceUrl}
                onChange={(e) => setReferenceUrl(e.target.value)}
                placeholder="https://… (pega un link a una foto que te gustaría usar como referencia)"
                className="mt-1 text-sm"
                data-testid="suggest-reference"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
              <Button variant="outline" onClick={onClose}>Cancelar</Button>
              <Button
                onClick={submit}
                disabled={submitting || loading}
                className="bg-amber-600 hover:bg-amber-700 text-white gap-1"
                data-testid="suggest-submit"
              >
                {submitting ? "Enviando…" : "Enviar a Marketing"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
